/**
 * La demande d'ouvrir la feuille de profil du tuteur, venue d'ailleurs que de son avatar.
 *
 * « Mon compte » n'est plus une page : c'est la feuille que l'avatar de l'en-tête ouvre. Une route
 * `account` poussée (le retour de l'inscription Stripe, `/account?redirect_from_stripe_onboarding=1`,
 * ou le repli d'un écran ouvert à froid) ramène donc à la coquille et lui demande d'ouvrir la feuille.
 * L'en-tête de chaque onglet est monté : seul celui de l'onglet visible prend la demande, une fois.
 */
import { useIsFocused } from "@react-navigation/native"
import { useEffect, useRef } from "react"

/**
 * Une demande non prise expire : la coquille la prend aussitôt revenue, et une demande restée en
 * mémoire du module ouvrirait la feuille du tuteur suivant connecté sur l'appareil.
 */
export const TUTOR_PROFILE_SHEET_REQUEST_TTL_MS = 10_000

let requestedAt: number | null = null
const listeners = new Set<() => void>()

/** Demande l'ouverture : l'en-tête visible la prend tout de suite, sinon dès qu'il redevient visible. */
export function requestTutorProfileSheet(): void {
    requestedAt = Date.now()
    listeners.forEach((listener) => listener())
}

/** Prend la demande en cours, s'il y en a une encore fraîche : elle ne sert qu'une fois. */
export function takeTutorProfileSheetRequest(): boolean {
    const taken = requestedAt != null && Date.now() - requestedAt <= TUTOR_PROFILE_SHEET_REQUEST_TTL_MS
    requestedAt = null
    return taken
}

/** L'en-tête de la coquille : ouvre sa feuille sur une demande, tant que son onglet est visible. */
export function useTutorProfileSheetRequest(open: () => void): void {
    const focused = useIsFocused()
    const openRef = useRef(open)
    openRef.current = open

    useEffect(() => {
        if (!focused) return
        const consume = () => {
            if (takeTutorProfileSheetRequest()) openRef.current()
        }
        consume()
        listeners.add(consume)
        return () => {
            listeners.delete(consume)
        }
    }, [focused])
}
